import React from 'react';
import { Check, X } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ErrorBar,
} from 'recharts';

interface BootstrapMetric {
  channel: string;
  estimate: number;
  lowerCI: number;
  upperCI: number;
  error: number;
}

interface BootstrappedMetricsProps {
  data: BootstrapMetric[] | undefined;
  metricName?: string;
  title?: string;
}

export const BootstrappedMetrics: React.FC<BootstrappedMetricsProps> = ({
  data,
  metricName = 'Impact',
  title = 'Channel Impact with Confidence Intervals (95% CI)',
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-96 bg-gray-50 rounded">
          <p className="text-gray-500">No uncertainty metrics available from model results</p>
        </div>
      </div>
    );
  }

  const chartData = data;

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="channel" />
          <YAxis label={{ value: metricName, angle: -90, position: 'insideLeft' }} />
          <Tooltip
            formatter={(value: any) => value.toFixed(3)}
            contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
          />
          <Legend />
          <Bar
            dataKey="estimate"
            fill="#8884d8"
            name={`${metricName} (Estimate)`}
            radius={[8, 8, 0, 0]}
          >
            <ErrorBar
              dataKey="error"
              width={4}
              strokeWidth={2}
              stroke="#ff7300"
              direction="y"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-900">Channel</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Estimate</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Lower 95% CI</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Upper 95% CI</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-900">Std Error</th>
              <th className="px-4 py-3 text-center font-semibold text-gray-900">Significant?</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((metric, idx) => {
              const isSignificant = metric.lowerCI > 0 || metric.upperCI < 0;
              return (
                <tr
                  key={metric.channel}
                  className={`border-b border-gray-200 hover:bg-gray-50 ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{metric.channel}</td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-600">
                    {metric.estimate.toFixed(3)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {metric.lowerCI.toFixed(3)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {metric.upperCI.toFixed(3)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {(metric.error / 1.96).toFixed(3)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        isSignificant
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {isSignificant ? (
                        <><Check className="w-3 h-3 inline mr-1" /> Yes</>
                      ) : (
                        <><X className="w-3 h-3 inline mr-1" /> No</>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p>
          <strong>What this shows:</strong> Each channel's estimated impact with a range showing how confident we are in that estimate.
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>
            <strong>Estimate:</strong> Best estimate of each channel's impact
          </li>
          <li>
            <strong>95% Range:</strong> We're 95% confident the true value falls within this range
          </li>
          <li>
            <strong>Significant?:</strong> "Yes" means we're confident the channel has a real effect (the range doesn't include zero)
          </li>
        </ul>
        <p className="mt-2">
          <strong>Tip:</strong> Wide ranges = more uncertainty. Narrow ranges = more reliable estimates.
          Focus budget on channels that are both high-impact AND significant.
        </p>
      </div>
    </div>
  );
};
